/*
  Project Structure · Import wizard (UI overhaul wave 2, screen 08 idea 10).

  Upload › Map columns › Preview › Apply instead of one "Importing replaces the structure" confirm:
  - Upload: a TTL (ETS project), an ETS CSV / Excel export, or Sara's own structure export.
  - Map columns (CSV / ETS Excel only): which column holds locations & addresses, part numbers, product
    and application — with the file's first rows to look at. When the default columns (D/G/I/M) find no
    hierarchy the preview answers with `parse_error` and the file's rows, and the wizard opens this step with
    that message instead of stopping at Upload.
  - Preview: POST …/execution-structure/import-preview (a dry run): adds or replaces, counts before → after,
    the added / removed buildings, floors, rooms, panels, devices, and how the execution sub-items change.
  - Apply: POST …/execution-structure/import-file with the same file and the mapping of the preview on screen
    (one history entry), so what is applied is what was previewed.

    <StructureImportWizard open={open} onOpenChange={setOpen} projectId={id} isBms={isBms}
      dirtyCount={structureDiff.count} onSaveFirst={saveStructure} onApplied={(project, file) => …} />
*/
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Check, ChevronDown, FileSpreadsheet, Info, Loader2, Replace, SquarePlus, TriangleAlert, Upload,
} from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { MONO } from './ProjectUi';
import { StructureImportWarnings } from './StructureImportWarnings';
import { IMPORT_LEVELS, IMPORT_MAPPING_FIELDS, columnLetter, summarizeImportPreview } from './structureModel';

const STEPS = [
  { key: 'upload', label: 'Upload' },
  { key: 'map', label: 'Map columns' },
  { key: 'preview', label: 'Preview' },
  { key: 'apply', label: 'Apply' },
];
const NOT_IN_FILE = 'none';
const whole = (value) => formatNumber(value, { decimals: 0 });
/** "columns D, G, I and M" / "column D" — what a mapping reads (fields not in the file are left out). */
const describeMapping = (mapping) => {
  const letters = IMPORT_MAPPING_FIELDS.map((field) => mapping?.[field.key]).filter((index) => index !== null && index !== undefined).map(columnLetter);
  return letters.length > 1 ? `columns ${letters.slice(0, -1).join(', ')} and ${letters.at(-1)}` : `column ${letters.join('')}`;
};

function Stepper({ step, mappable }) {
  const current = STEPS.findIndex((item) => item.key === step);
  return (
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]" aria-label="Import steps">
      {STEPS.map((item, index) => {
        const done = index < current;
        const active = index === current;
        const skipped = item.key === 'map' && mappable === false && current > 1;
        return (
          <li key={item.key} className="flex items-center gap-2">
            {index > 0 ? <span className="h-px w-5 bg-line" aria-hidden="true" /> : null}
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-semibold',
                active ? 'bg-brand text-brand-fg' : done ? 'text-brand-ink' : 'text-ink-3',
                skipped && 'text-ink-4 line-through'
              )}
              aria-current={active ? 'step' : undefined}
            >
              <span className={cn('grid h-[18px] w-[18px] place-items-center rounded-full text-[11px]', active ? 'bg-white/20' : done ? 'bg-brand-soft' : 'bg-[color:var(--status-gray-bg)]')}>
                {done ? <Check className="h-3 w-3" aria-hidden="true" /> : index + 1}
              </span>
              {item.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function ColumnSelect({ field, value, columnCount, sampleHeader, onChange }) {
  const options = Array.from({ length: Math.max(columnCount, 1) }, (_, index) => index);
  return (
    <label className="block space-y-1">
      <span className="text-[12.5px] font-semibold text-ink-2">
        {field.label}
        {field.required ? <span className="text-status-red-ink"> *</span> : null}
      </span>
      <Select value={value === null || value === undefined ? NOT_IN_FILE : String(value)} onValueChange={(next) => onChange(next === NOT_IN_FILE ? null : Number(next))}>
        <SelectTrigger aria-label={field.label} className="h-9 rounded-lg bg-surface text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {!field.required ? <SelectItem value={NOT_IN_FILE}>Not in this file</SelectItem> : null}
          {options.map((index) => (
            <SelectItem key={index} value={String(index)}>
              Column {columnLetter(index)}{sampleHeader[index] ? ` — ${String(sampleHeader[index]).trim().slice(0, 28)}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {field.hint ? <span className="block text-[11.5px] text-ink-3">{field.hint}</span> : null}
    </label>
  );
}

function SampleTable({ columns }) {
  const sample = columns?.sample || [];
  const width = Math.min(Math.max(...sample.map((row) => row.length), 0), 16);
  const mapped = new Map(Object.entries(columns?.mapping || {}).filter(([, index]) => index !== null).map(([key, index]) => [index, key]));
  if (!sample.length) return <p className="text-[12.5px] text-ink-3">The file has no rows to show.</p>;
  return (
    <div className="max-h-[220px] overflow-auto rounded-lg border border-line">
      <table className="w-full border-separate border-spacing-0 text-[12px]">
        <thead>
          <tr>
            {Array.from({ length: width }, (_, index) => (
              <th key={index} className={cn('sticky top-0 border-b border-line bg-surface-2 px-2 py-1 text-left font-bold', mapped.has(index) ? 'text-brand-ink' : 'text-ink-3')}>
                {columnLetter(index)}
                {mapped.has(index) ? <span className="ml-1 font-semibold">· {IMPORT_MAPPING_FIELDS.find((field) => field.key === mapped.get(index))?.label.split(' ')[0]}</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sample.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: width }, (_, index) => (
                <td key={index} className={cn('max-w-[160px] truncate whitespace-pre border-b border-line-2 px-2 py-1', mapped.has(index) ? 'bg-brand-soft text-ink' : 'text-ink-3')} title={row[index] || ''}>
                  {row[index] || ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NameList({ title, tone, entries }) {
  const [open, setOpen] = useState(false);
  const total = entries.reduce((sum, entry) => sum + entry.count, 0);
  if (!total) return null;
  return (
    <div className="rounded-lg border border-line">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[13px] font-semibold text-ink hover:bg-surface-hover"
      >
        <span className="inline-flex items-center gap-2">
          <StatusPill tone={tone} label={`${tone === 'red' ? '−' : '+'}${whole(total)}`} size="sm" />
          {title}
        </span>
        <ChevronDown className={cn('h-4 w-4 text-ink-3 transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>
      {open ? (
        <div className="max-h-[200px] space-y-2 overflow-y-auto border-t border-line-2 px-3 py-2">
          {entries.filter((entry) => entry.count).map((entry) => (
            <div key={entry.key}>
              <p className="text-[11px] font-bold uppercase tracking-[0.04em] text-ink-3">{entry.label} · {whole(entry.count)}</p>
              <ul className="mt-0.5 space-y-0.5 text-[12.5px] text-ink-2">
                {entry.names.map((name) => <li key={name} className="truncate" title={name}>{name}</li>)}
                {entry.count > entry.names.length ? <li className="text-ink-3">+ {whole(entry.count - entry.names.length)} more</li> : null}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PreviewStep({ preview, isBms }) {
  const replace = preview.mode === 'replace';
  const levelLabel = (key, label) => (isBms && key === 'floors' ? 'Subs' : label);
  const listEntries = (side) => IMPORT_LEVELS.map(({ key, label }) => ({
    key,
    label: levelLabel(key, label),
    count: Number(preview[side]?.[key]?.count || 0),
    names: preview[side]?.[key]?.names || [],
  }));
  const execution = preview.execution || {};
  const executionDelta = Number(execution.after || 0) - Number(execution.before || 0);
  return (
    <div className="space-y-4" data-testid="structure-import-preview">
      <div className={cn('flex items-start gap-3 rounded-xl border px-3.5 py-3', replace ? 'border-status-amber/40 bg-status-amber-bg' : 'border-line bg-surface-2')}>
        <span className={cn('ui-icon-tile h-9 w-9 shrink-0', replace ? 'tone-amber' : 'tone-navy')} aria-hidden="true">
          {replace ? <Replace className="h-4 w-4" /> : <SquarePlus className="h-4 w-4" />}
        </span>
        <div className="min-w-0 text-[13px] leading-snug text-ink-2">
          <p className="font-semibold text-ink">
            {replace ? 'This file replaces the project structure' : 'This file adds to the project structure'}
            <span className="ml-2 font-normal text-ink-3">{summarizeImportPreview(preview)}</span>
          </p>
          <p className="mt-0.5">
            {replace
              ? 'Everything in the structure today is swapped for what is in the file.'
              : 'New buildings, floors, rooms and devices are added; what is already there stays. Devices whose address already exists are skipped.'}
          </p>
        </div>
      </div>

      {/* BUG-010: a building of the file without a name is imported as "Building" — say so before Apply */}
      <StructureImportWarnings warnings={preview.warnings} />

      <table className="w-full border-separate border-spacing-0 overflow-hidden rounded-xl border border-line text-[13px]">
        <thead>
          <tr className="text-left text-[11.5px] font-bold text-ink-3">
            <th className="border-b border-line bg-surface-2 px-3 py-2">Level</th>
            <th className="border-b border-line bg-surface-2 px-3 py-2 text-right">Now</th>
            <th className="border-b border-line bg-surface-2 px-3 py-2 text-right">After import</th>
            <th className="border-b border-line bg-surface-2 px-3 py-2 text-right">Change</th>
          </tr>
        </thead>
        <tbody>
          {IMPORT_LEVELS.map(({ key, label }) => {
            const before = Number(preview.before?.[key] || 0);
            const after = Number(preview.after?.[key] || 0);
            const delta = after - before;
            return (
              <tr key={key}>
                <td className="border-b border-line-2 px-3 py-2 font-semibold text-ink">{levelLabel(key, label)}</td>
                <td className="border-b border-line-2 px-3 py-2 text-right tabular-nums text-ink-2">{whole(before)}</td>
                <td className="border-b border-line-2 px-3 py-2 text-right font-semibold tabular-nums text-ink">{whole(after)}</td>
                <td className={cn('border-b border-line-2 px-3 py-2 text-right font-semibold tabular-nums', delta > 0 ? 'text-status-green-ink' : delta < 0 ? 'text-status-red-ink' : 'text-ink-3')}>
                  {delta > 0 ? `+${whole(delta)}` : delta < 0 ? `−${whole(-delta)}` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="grid gap-2 sm:grid-cols-2">
        <NameList title="Added" tone="green" entries={listEntries('added')} />
        <NameList title="Removed" tone="red" entries={listEntries('removed')} />
      </div>

      {execution.enabled ? (
        <div className="rounded-xl border border-line px-3.5 py-3 text-[13px]" data-testid="structure-import-execution">
          <p className="font-semibold text-ink">
            Execution sub-items: {whole(execution.before)} → {whole(execution.after)}
            <span className={cn('ml-2', executionDelta > 0 ? 'text-status-green-ink' : executionDelta < 0 ? 'text-status-red-ink' : 'text-ink-3')}>
              {executionDelta > 0 ? `+${whole(executionDelta)}` : executionDelta < 0 ? `−${whole(-executionDelta)}` : 'no change'}
            </span>
          </p>
          {(execution.tasks || []).length ? (
            <ul className="mt-1.5 grid gap-x-6 gap-y-0.5 text-[12.5px] text-ink-2 sm:grid-cols-2">
              {execution.tasks.slice(0, 14).map((task) => (
                <li key={task.task} className="flex justify-between gap-3">
                  <span className="truncate">{task.task}</span>
                  <span className="shrink-0 tabular-nums text-ink-3">{whole(task.before)} → <b className="font-semibold text-ink">{whole(task.after)}</b></span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function StructureImportWizard({ open, onOpenChange, projectId, isBms = false, dirtyCount = 0, onSaveFirst, onApplied }) {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [preview, setPreview] = useState(null);
  // The mapping the preview on screen was read with (null = the file's default columns); Apply sends this one.
  const [previewMapping, setPreviewMapping] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [savingFirst, setSavingFirst] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep('upload');
    setFile(null);
    setMapping(null);
    setPreview(null);
    setPreviewMapping(null);
    setError('');
    setBusy(false);
  }, [open]);

  const formFor = (chosenFile, chosenMapping) => {
    const form = new FormData();
    form.append('file', chosenFile);
    if (chosenMapping) form.append('mapping', JSON.stringify(chosenMapping));
    return form;
  };

  const runPreview = async (chosenFile, chosenMapping, nextStep) => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/projects/${projectId}/execution-structure/import-preview`, formFor(chosenFile, chosenMapping), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data || {};
      setPreview(data);
      setPreviewMapping(chosenMapping);
      if (data.columns?.mappable) setMapping(chosenMapping || data.columns.mapping);
      // Nothing found with these columns: stay on (or open) Map columns, whatever step was asked for.
      if (data.parse_error && data.columns?.mappable) setStep('map');
      else setStep(nextStep || (data.columns?.mappable ? 'map' : 'preview'));
    } catch (err) {
      setError(getErrorMessage(err, 'Could not read this file'));
    } finally {
      setBusy(false);
    }
  };

  const chooseFile = (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    setMapping(null);
    setPreview(null);
    setPreviewMapping(null);
    void runPreview(chosen, null);
  };

  const apply = async () => {
    if (!file || !preview || preview.parse_error) return;
    setStep('apply');
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/projects/${projectId}/execution-structure/import-file`, formFor(file, previewMapping), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await onApplied?.(res.data, file);
      onOpenChange(false);
    } catch (err) {
      setError(getErrorMessage(err, 'The import failed — nothing was changed'));
      setStep('preview');
    } finally {
      setBusy(false);
    }
  };

  const saveFirst = async () => {
    if (!onSaveFirst) return;
    setSavingFirst(true);
    try {
      await onSaveFirst();
      if (file) await runPreview(file, previewMapping, 'preview');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the structure'));
    } finally {
      setSavingFirst(false);
    }
  };

  const columns = preview?.columns;
  const mappingChanged = useMemo(
    () => Boolean(columns?.mappable && mapping && JSON.stringify(mapping) !== JSON.stringify(columns.mapping)),
    [columns, mapping]
  );
  const header = columns?.sample?.[0] || [];
  const parseError = preview?.parse_error || '';
  const canPreview = Boolean(mapping && mapping.label !== null && mapping.label !== undefined) && (mappingChanged || !parseError);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy || !next) onOpenChange(next); }}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto rounded-2xl border-line bg-surface p-0 text-ink" data-testid="structure-import-wizard">
        <DialogHeader className="min-w-0 space-y-2 border-b border-line px-6 pb-4 pt-5 text-left">
          <DialogTitle className="font-heading text-[18px] font-extrabold">Import project structure</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-3">
            {file ? <>File <span className={cn(MONO, 'text-ink-2')}>{file.name}</span> — nothing changes until you press Apply.</> : 'Nothing changes until you press Apply on the last step.'}
          </DialogDescription>
          <Stepper step={step} mappable={columns ? columns.mappable : null} />
        </DialogHeader>

        {/* min-w-0: the wide sample table scrolls inside its box instead of widening the dialog on phones */}
        <div className="min-w-0 space-y-4 px-6 py-5">
          {error ? (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-[color:var(--danger-border,#f3c2c2)] bg-status-red-bg px-3 py-2 text-[13px] text-status-red-ink" data-testid="structure-import-error">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          {step === 'upload' ? (
            <div className="space-y-3">
              <input
                ref={inputRef}
                type="file"
                accept=".ttl,.csv,.xlsx,.xls"
                className="hidden"
                data-testid="structure-import-file"
                onChange={(event) => {
                  const chosen = event.target.files?.[0];
                  event.target.value = '';
                  chooseFile(chosen);
                }}
              />
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => { event.preventDefault(); chooseFile(event.dataTransfer.files?.[0]); }}
                disabled={busy}
                className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-[color:var(--line-dashed,#d7d8e6)] bg-surface-2 px-6 py-8 text-center hover:border-[color:var(--brand-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {busy ? <Loader2 className="h-6 w-6 animate-spin text-brand-ink" aria-hidden="true" /> : <Upload className="h-6 w-6 text-brand-ink" aria-hidden="true" />}
                <span className="text-[14px] font-semibold text-ink">{busy ? 'Reading the file…' : 'Choose a file or drop it here'}</span>
                <span className="text-[12.5px] text-ink-3">{isBms ? 'Excel (.xlsx, .xls)' : 'TTL, CSV or Excel (.ttl, .csv, .xlsx, .xls)'}</span>
              </button>
              <ul className="grid gap-2 text-[12.5px] text-ink-3 sm:grid-cols-3">
                <li className="rounded-lg border border-line px-3 py-2"><b className="block text-ink-2">ETS export (CSV / Excel)</b>Adds buildings, floors, rooms and devices; you can map its columns.</li>
                <li className="rounded-lg border border-line px-3 py-2"><b className="block text-ink-2">ETS project (TTL)</b>Replaces the whole structure with the project in the file.</li>
                <li className="rounded-lg border border-line px-3 py-2"><b className="block text-ink-2">Sara structure export</b>The Excel from “Export Excel” — replaces the structure.</li>
              </ul>
            </div>
          ) : null}

          {step === 'map' && columns?.mappable ? (
            <div className="space-y-4" data-testid="structure-import-mapping">
              <p className="flex items-start gap-2 text-[13px] text-ink-2">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
                Tell the import which columns hold what. The highlighted columns are read; the first rows of the file are below.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                {IMPORT_MAPPING_FIELDS.map((field) => (
                  <ColumnSelect
                    key={field.key}
                    field={field}
                    value={mapping?.[field.key]}
                    columnCount={columns.column_count}
                    sampleHeader={header}
                    onChange={(next) => setMapping((prev) => ({ ...(prev || columns.mapping), [field.key]: next }))}
                  />
                ))}
              </div>
              <SampleTable columns={{ ...columns, mapping: mapping || columns.mapping }} />
              {parseError ? (
                <p className="flex items-start gap-2 rounded-lg border border-status-amber/40 bg-status-amber-bg px-3 py-2 text-[12.5px] text-ink-2" role="status" data-testid="structure-import-nothing-found">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-amber-ink" aria-hidden="true" />
                  <span>
                    <b className="font-semibold text-ink">
                      {previewMapping ? 'Nothing found with these columns.' : `Nothing found in the usual ${describeMapping(columns.mapping)}.`}
                    </b>{' '}
                    {mappingChanged ? 'Press Preview to read the file again with your choice.' : 'Pick the columns that hold the locations and device addresses, then press Preview.'}
                    <span className="mt-0.5 block text-ink-3">{parseError}</span>
                  </span>
                </p>
              ) : (
                <p className="text-[12.5px] text-ink-3">
                  With the current columns: <b className="font-semibold text-ink-2">{summarizeImportPreview(preview)}</b>
                  {mappingChanged ? ' — press Preview to read the file again with your choice.' : '.'}
                </p>
              )}
            </div>
          ) : null}

          {(step === 'preview' || step === 'apply') && preview ? (
            <>
              {dirtyCount > 0 ? (
                <div className="flex flex-wrap items-center gap-3 rounded-xl border border-status-amber/40 bg-status-amber-bg px-3.5 py-3 text-[13px]" data-testid="structure-import-dirty">
                  <TriangleAlert className="h-4 w-4 shrink-0 text-status-amber-ink" aria-hidden="true" />
                  <span className="min-w-0 flex-1 text-ink-2">
                    <b className="font-semibold text-ink">{dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}</b> in the structure.
                    The import reads the saved structure, so save them first (or discard them on the page).
                  </span>
                  {onSaveFirst ? (
                    <Button type="button" size="sm" variant="outline" onClick={saveFirst} disabled={savingFirst}>
                      {savingFirst ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
                      Save structure first
                    </Button>
                  ) : null}
                </div>
              ) : null}
              <PreviewStep preview={preview} isBms={isBms} />
            </>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-line px-6 py-4">
          <div>
            {step === 'map' ? (
              <Button type="button" variant="ghost" onClick={() => setStep('upload')} disabled={busy}>
                <ArrowLeft aria-hidden="true" />
                Choose another file
              </Button>
            ) : null}
            {step === 'preview' ? (
              <Button type="button" variant="ghost" onClick={() => setStep(columns?.mappable ? 'map' : 'upload')} disabled={busy}>
                <ArrowLeft aria-hidden="true" />
                {columns?.mappable ? 'Back to columns' : 'Choose another file'}
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy && step === 'apply'}>Cancel</Button>
            {step === 'map' ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => (mappingChanged ? runPreview(file, mapping, 'preview') : setStep('preview'))}
                disabled={busy || !canPreview}
                title={!canPreview && parseError ? 'Choose different columns first' : undefined}
                data-testid="structure-import-to-preview"
              >
                {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <FileSpreadsheet aria-hidden="true" />}
                Preview
                <ArrowRight aria-hidden="true" />
              </Button>
            ) : null}
            {step === 'preview' || step === 'apply' ? (
              <Button
                type="button"
                onClick={apply}
                disabled={busy || dirtyCount > 0 || !preview || Boolean(parseError)}
                title={dirtyCount > 0 ? 'Save or discard your structure changes first' : undefined}
                data-testid="structure-import-apply"
              >
                {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Check aria-hidden="true" />}
                {preview?.mode === 'replace' ? 'Replace structure' : 'Apply import'}
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
